import React, { useState, useEffect, ChangeEvent } from "react";
import Box from "@mui/material/Box";
import { useTranslation } from "react-i18next";
import { IMessageBoxParams } from "../../../../../common/MessageBox";
import { IConfirmDialogParams } from "../../../../../common/ConfirmDialog";
import { getProjects, IProjectRow } from "../../../../../webapi/service/getProjects";
import { WebAPI } from "../../../../../webapi/constants";
import { getSimulator, ISimulatorRow } from "../../../../../webapi/service/getSimulator";
import { Button } from "react-bootstrap";
import BackButton from "../../../common/BackButton";
import UploadSettingFileButton from "../file/UploadSettingFileButton";
import ResetFileButton from "../file/ResetFileButton";
import DownloadSFileButton from "../file/DownloadConfFileButton";
import "../../../../main.css";
import "./fkdppsettings.css";
import "../../projectdetail.css";
import { 
  FormControlLabel,
  RadioGroup, 
  Radio, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  TextField } from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import AuthService from "../../../../../common/AuthService";
import { IFileRow } from "../../../../../webapi/service/getFIles";
import { AppConfig, ConfigurationFileType, ControlMode, ListConfig, ProjectStatus, SettingType } from "../../../../../common/constants";
import { deleteConfFile } from "../../../../../webapi/service/deleteConfFile";
import { uploadConfFile } from "../../../../../webapi/service/uploadConfFile";
import { downloadConfFile } from "../../../../../webapi/service/downloadConfFile";
import { getRevision } from "../../../../../webapi/service/getRevision";
import { trainModel } from "../../../../../webapi/service/trainModel";
import { stopTrainModel } from "../../../../../webapi/service/stopTrainModel";
import { ModelConstants } from "./constants";
import { getFileLists } from "../../../../../webapi/service/getFileLists";
import { getConfFile } from "../../../../../webapi/service/getConfFile";
import { truncation } from "./truncation";

export interface IProps {
  /**
   * 認証サービス
   */
  authService?: AuthService;
  /**
   * 選択されたプロジェクト情報
   */
  projectInfo: IProjectRow;
  /**
   * ファイル情報一覧
   */
  rows: IFileRow[];
  /**
   * 戻るボタンが押された場合に呼び出されます
   */
  onBack: () => void;
  /**
   * プロジェクト一覧画面に戻る場合に呼び出します
   */
  onTop: () => void;
  /**
   * ローディング画面の表示非表示を切り替えます
   * @param loading - ローディング画面を表示する場合はtrue
   */
  onLoading: (loading: boolean) => void;
  /**
   * メッセージBoxを表示します
   * @param params - メッセージBoxパラメータ
   */
  showMessage: (params: IMessageBoxParams) => void;
  /**
   * 確認ダイアログを表示します。
   * @param params - 確認ダイアログパラメータ
   */
  showConfirm: (param: IConfirmDialogParams) => void;
  /**
   * 通信エラーが発生した場合に呼び出します
   * @param id - メッセージID
   * @param status - httpステータスコード
   */
  onCommunicationError: (
    id: string | undefined,
    status: number | undefined
  ) => void;
  /**
   * 非操作タイムアウトをリセットする場合に呼び出します
   */
  resetUnoperatedTimeout: () => void;
}

export default function FkdppSettingsFormContents(props : IProps): JSX.Element {
  const { t } = useTranslation();
  const intervalOptions: string[] = ["1", "2", "5", "10", "20", "50", "100", "200"];
  const type_auto = SettingType.AUTO;
  const type_custom = SettingType.CUSTOM;
  const fkdppconf = ConfigurationFileType.TRAIN_FKDPPCONF;
  const simconf = ConfigurationFileType.TRAIN_SIMCONF;
  const rewardconf = ConfigurationFileType.TRAIN_REWARDCONF;
  const direct = ControlMode.DIRECT;
  const indirect = ControlMode.INDIRECT;
  const fkdppintervalmax = ModelConstants.FKDPPINTERVAL_MAX;
  const fkdppintervalmin = ModelConstants.FKDPPINTERVAL_MIN;
  const spmax = ModelConstants.SP_MAX;
  const spmin = ModelConstants.SP_MIN;
  const pbmax = ModelConstants.PB_MAX;
  const pbmin = ModelConstants.PB_MIN;
  const pbinit = ModelConstants.PB_INIT;
  const exptimemax = ModelConstants.EXPTIME_MAX;
  const exptimemin = ModelConstants.EXPTIME_MIN;
  const timax = ModelConstants.TI_MAX;
  const timin = ModelConstants.TI_MIN;
  const tiinit = ModelConstants.TI_INIT;
  const tdmax = ModelConstants.TD_MAX;
  const tdmin = ModelConstants.TD_MIN;
  const tdinit = ModelConstants.TD_INIT;
  const pidinterval_init = ModelConstants.PIDINTERVAL_INIT
  const rangemax_max = ModelConstants.RANGEMAX_MAX;
  const rangemax_min = ModelConstants.RANGEMAX_MIN;
  const rangemin_max = ModelConstants.RANGEMIN_MAX;
  const rangemin_min = ModelConstants.RANGEMIN_MIN;
  const StatusDoing = ProjectStatus.DOING;
  const StatusDone = ProjectStatus.DONE;
  const MenuProps = ListConfig.MENU_PROPS;
  const label_auto = t("fkdpp_settings_automode");
  const label_custom = t("fkdpp_settings_custommode");
  
  const { 
    authService,
    projectInfo,
    rows,
    onBack,
    onTop,
    onLoading,
    showMessage,
    showConfirm,
    onCommunicationError,
    resetUnoperatedTimeout,
  } = props;
  const projectid =  projectInfo.projectid;  

  useEffect(() => {
    loadRev();
    loadFiles();
    updateProjectInfo();
    loadSimulatorsInfo();
    setInitialValue();
    loadConffile(fkdppconf);
    loadConffile(simconf);
    loadConffile(rewardconf);
  }, []);

  const [currentProjectInfo, setCurrentProjectInfo] = useState<IProjectRow>();
  /**
   * 最新のプロジェクト情報を取得し各種ステータスにセットします
   */
  function updateProjectInfo() {
    onLoading(true);
    getProjects({
      authService: authService,
      success: (rows) => {
        const newInfo = rows.find((row) => {
          if (row.projectid === projectid) {
            return row;
          }
        });
        if (newInfo) setCurrentProjectInfo(newInfo);          
        onLoading(false);      
      },
      fail: (id, status) => {
        onCommunicationError(id, status),
        onLoading(false);
      },
    });
  }

  const [files, setFiles] = useState<IFileRow[]>(rows);
  /**
   * データファイルをロードします
   * 1つもなかった場合はダイアログを表示し、ファイル一覧画面に戻ります
   */
  function loadFiles() {
    getFileLists({
      authService: authService,
      projectid: projectid,
      success: (data) => {
        if (data.length === 0) {
          showMessage({
            title: t("fkdpp_settings_nofile"),
            message: t("fkdpp_settings_nofile_message"),
            onClose:() => onBack(),
          });
        } else {
          setFiles(data);
        }        
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  }

  //ファイル情報から選択肢を生成する
  const fileList = files.map((listvalue) => {
    return (
      <MenuItem key={listvalue.id} value={listvalue.fileid}>
        {listvalue.name}
      </MenuItem>
    );
  });

  //PID制御周期のリストを生成する
  const intervalOptionsList = intervalOptions.map((value, i) => {
    return (
      <MenuItem key={i} value={value}>
        {value}
      </MenuItem>
    );
  });

  //シミュレータを取得する
  const [simulatorsInfo, setSimulatorsInfo] = useState<ISimulatorRow[]>([]);
  const loadSimulatorsInfo = () => {
    onLoading(true);
    getSimulator({
      authService: authService,
      projectid: projectid,
      success: (simulatorsInfo) => {
        if (simulatorsInfo.length === 0) {
          showMessage({
            title: t("fkdpp_settings_nosimulator"),
            message: t("fkdpp_settings_nosimulator_messagage"),
            onClose: () => onBack(),
          });
        } else {
          setSimulatorsInfo(simulatorsInfo);
          setTimeout(() => {
            onLoading(false);
          });
        }
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  }

  //シミュレータ一覧から選択肢を生成する
  const simulatorList = simulatorsInfo.map((listvalue) => {
    return (
      <MenuItem key={listvalue.id} value={listvalue.simulatorid}>
        {listvalue.name}
      </MenuItem>
    );
  });

  //Rev番号を取得する
  const [rev, setRev] = useState<string[]>([]);
  const loadRev = () => {
    onLoading(true);
    getRevision({
      authService: authService,
      success: (row) => {
        setRev(row.revision);
        onLoading(false);     
      },
      fail: (id, status) => {
        onCommunicationError(id, status)
        onLoading(false);
      }
    });    
  }

  //Rev番号一覧から選択肢を生成する
  const revList = rev.map((listvalue) => {
    return (
      <MenuItem key={listvalue} value={listvalue}>
        {listvalue}
      </MenuItem>
    );
  });

  //アップロード中はtrue
  const [uploading, setUploading] = useState<boolean>(false);

  //設定ファイル取得
  const [fkdppconffilename, setFkdppconffilename] = useState<string>("");
  const [simconffilename, setSimconffilename] = useState<string>("");
  const [rewardconffilename, setRewardconffilename] = useState<string>("");
  const loadConffile = (type: string) => {
    onLoading(true);
    getConfFile({
      authService: authService,
      projectid: projectid,
      type: type,
      success: (row) => {
        if (type === fkdppconf) {
          setFkdppconffilename(row.name);
        } else if (type === simconf) {
          setSimconffilename(row.name);
        } else if (type === rewardconf) {
          setRewardconffilename(row.name);
        }
        onLoading(false);
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  }

  //設定ファイルリセット
  const handleResetConfFile = (type: string) => {
    onLoading(true);
    resetUnoperatedTimeout();
    setUploading(true);
    deleteConfFile({
      authService: authService,
      projectid: projectid,
      type: type,
      success: () => {
        setUploading(false);
        onLoading(false);
        loadConffile(type);
      },
      fail: (id, status) => {
        setUploading(false);
        onCommunicationError(id, status);
      },
    });
  }

  //設定ファイルアップロード
  const handleSelectConfFile = (
    event: React.ChangeEvent<HTMLInputElement>, 
    type: string
    ) => {
    const files = event.target.files;
    if(!files) {
      return;
    }
    if (files.length > AppConfig.MAX_SETTING_FILE) {
      onCommunicationError(WebAPI.ID_NUM_FILE_EXCEED, undefined);
      return;
    }
    for (let i = 0; i < files.length; i++) {
      if (files[i].size > AppConfig.MAX_UPLOAD_FILE_SIZE) { 
        onCommunicationError(WebAPI.ID_FILE_TOO_LARGE, undefined);
        return;
      }
    }
    onLoading(true);
    resetUnoperatedTimeout();
    setUploading(true);
    const fileList: File[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      fileList.push(file);
    }
    uploadConfFile({
      authService: authService,
      projectid: projectid,
      files: fileList,
      type: type,
      success: () => {
        setUploading(false);
        onLoading(false);
        loadConffile(type);
      },
      fail: (id, status) => {
        setUploading(false);
        onCommunicationError(id, status);
      },
    });  
  }

  //設定ファイルダウンロード
  const handleDownloadConfFile = (type: string) => {
    resetUnoperatedTimeout();
    downloadConfFile({
      authService: authService,
      projectid: projectid,
      type: type,
      filename: "conffile_" + type + "_" + projectid,
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  }

  //Rev番号
  const [revnum, setRevnum] = useState<string>("");
  const handleChangeRevnum = (event: SelectChangeEvent) => {
    setRevnum(event.target.value as string);
  }

  //学習モード
  const [learnmode, setLearnmode] = useState<string>(type_auto);
  const handleChangeLearnmode = (event: SelectChangeEvent) => {
    setLearnmode(event.target.value as string);
  }

  //制御周期
  const [fkdppinterval, setFkdppinterval] = useState<number>();
  const [ffkdppinterval, setFfkdppinterval] = useState<number|string>();
  const [inputFkdppintervalErr, setInputFkdppintervalErr] = useState<boolean>(false);
  const handleChangeFkdppinterval = (event: ChangeEvent<HTMLInputElement>) => {  
    const str = event.target.value;
    if (!isNaN(Number(str)) && str.length >= 1) { 
      //非数でなく、1文字以上入力有
      if (Number(str) >= fkdppintervalmin && Number(str) <= fkdppintervalmax) {
      const val = Math.round(Number(str));
        //値の上下限値範囲内
        setInputFkdppintervalErr(false);
        setFfkdppinterval(str);
        setFkdppinterval(val);
      } else {
        //値の上下限値範囲外
        setInputFkdppintervalErr(true);
        setFfkdppinterval(str);
        setFkdppinterval(undefined);
      }
    } else {
      //非数だった場合
      setInputFkdppintervalErr(true);
      setFfkdppinterval(str);
      setFkdppinterval(undefined);
    }
  }

  //目標値
  const [sp, setSp] = useState<number>();    //実際のsp値
  const [fsp, setFsp] = useState<number|string>();    //フォームに表示される値（マイナス記号や空欄に対応）
  const [inputSpErr, setInputSpErr] = useState<boolean>(false);   //エラー表示
  const [prevFsp, setPrevFsp] = useState<number|string>();    //フォームの一つ前の状態を保持
  const handleChangeSp = (event: ChangeEvent<HTMLInputElement>) => {
    setPrevFsp(fsp);
    const str = event.target.value;
    if (!isNaN(Number(str)) && str.length >= 1 && Number(str) >= spmin && Number(str) <= spmax) {
      const val = Math.round(Number(str) * Math.pow(10, 5)) / Math.pow(10, 5);
      setInputSpErr(false);
      setFsp(str);
      setSp(val);
    } else {
      setInputSpErr(true);
      setFsp(str);
      setSp(undefined);
    } 
  }

  //実験時間
  const [exp_time, setExp_time] = useState<number>();
  const [fexp_time, setFexp_time] = useState<number | string>();
  const [inputExptimeErr, setInputExptimeErr] = useState<boolean>(false);
  const handleChangeExptime = (event: ChangeEvent<HTMLInputElement>) => {
    const str = event.target.value;
    if (!isNaN(Number(str)) && str.length >= 1) {
      const val = Number(str);
      setInputExptimeErr(false);
      setFexp_time(str);
      setExp_time(val);
    } else {
      setInputExptimeErr(true);
      setFexp_time(str);
      setExp_time(undefined);
    }
  };

  /**
   * 実験時間の上下限値チェックを行います
   * 関係する値が変更された場合に実行したいので別の関数に分割
   */
  function checkExptime() {
    if (exp_time !== undefined) {
      const val = Math.round(exp_time * Math.pow(10, 2)) / Math.pow(10, 2);
      if (
        (autoForm === false || autoDirectForm === false) &&
        autoIndirectForm === true
      ) {
        if (exp_time < exptimemin || exp_time > exptimemax) {
          setInputExptimeErr(true);
          return;
        }
        //間接制御の場合
        val >= exptimemin && val <= exptimemax
          ? setInputExptimeErr(false)
          : setInputExptimeErr(true);
      } else {
        //直接制御の場合
        if (fkdppinterval) {
          exp_time >= Math.round(fkdppinterval / 1000 * 1 * Math.pow(10, 2)) / Math.pow(10, 2) &&
          exp_time <= Math.round(fkdppinterval / 1000 * 10000 * Math.pow(10, 2)) / Math.pow(10, 2)
            ? setInputExptimeErr(false)
            : setInputExptimeErr(true);
        }
      }
    }
  }

  //制御モード
  const [controlmode, setControlmode] = useState("");
  const handleChangeControlmode = (event: SelectChangeEvent) => {
    setControlmode(event.target.value as string);
  }

  //PID制御データのファイルID
  const [piddatafileid, setPiddataFileId] = useState("");
  const handleChangePiddataFileId = (event: SelectChangeEvent) => {
    setPiddataFileId(event.target.value as string); 
  }

  //使用するシミュレータID
  const [simulatorid, setSimulatorId] = useState("");
  const handleChangeSimulatorId = (event: SelectChangeEvent) => {
    setSimulatorId(event.target.value as string);
  }

  //比例帯[%]
  const [pb, setPb] = useState<number>();
  const [fpb, setFpb] = useState<number|string>(pbinit);
  const [inputPbErr, setInputPbErr] = useState<boolean>(false);
  const handleChangePb = (event: ChangeEvent<HTMLInputElement>) => {
    const str = event.target.value;
    if (!isNaN(Number(str)) && str.length >= 1) {
      if (Number(str) >= pbmin && Number(str) <= pbmax) {
      const pbval = Math.round(Number(str) * Math.pow(10, 1)) / Math.pow(10, 1);
        setInputPbErr(false);
        setFpb(str);
        setPb(pbval);
      } else {
        setInputPbErr(true);
        setFpb(str);
        setPb(undefined);
      }
    } else {
      setInputPbErr(true);
      setFpb(str);
      setPb(undefined);      
    }
  };

  //積分時間[sec]
  const [ti, setTi] = useState<number>();
  const [fti, setFti] = useState<number|string>(tiinit);
  const [inputTiErr, setInputTiErr] = useState<boolean>(false);
  const handleChangeTi = (event: ChangeEvent<HTMLInputElement>) => {
    const str = event.target.value;
    if (!isNaN(Number(str)) && str.length >= 1) {
      if (Number(str) >= timin && Number(str) <= timax) {
      const val = Math.round(Number(str));
        setInputTiErr(false);
        setFti(str);
        setTi(val);
      } else {
        setInputTiErr(true);
        setFti(str);
        setTi(undefined);
      }
    } else {
      setInputTiErr(true);
      setFti(str);
      setTi(undefined);      
    }
  };

  //微分時間[sec]
  const [td, setTd] = useState<number>();
  const [ftd, setFtd] = useState<number|string>(tdinit);
  const [inputTdErr, setInputTdErr] = useState<boolean>(false);
  const handleChangeTd = (event: ChangeEvent<HTMLInputElement>) => {
    const str = event.target.value;
    if (!isNaN(Number(str)) && str.length >= 1) {
      if (Number(str) >= tdmin && Number(str) <= tdmax) {
      const val = Math.round(Number(str));
        setInputTdErr(false);
        setFtd(str);
        setTd(val);
      } else {
        setInputTdErr(true);
        setFtd(str);
        setTd(undefined);        
      }
    } else {
      setInputTdErr(true);
      setFtd(str);
      setTd(undefined);
    }
  };

  //PID制御周期[msec]
  const [pidinterval, setPidinterval] = useState<number>();
  const [fpidinterval, setFpidinterval] = useState<string>(pidinterval_init);
  const handleChangePidinterval = (event: SelectChangeEvent) => {
    setPidinterval(Number(event.target.value) as number);
    setFpidinterval(event.target.value);
  }

  //制御レンジ上限
  const [rangemax, setRangemax] = useState<number>();
  const [frangemax, setFrangemax] = useState<number|string>();
  const [inputRmaxErr, setInputRmaxErr] = useState<boolean>(false);
  const handleChangeRangemax = (event: ChangeEvent<HTMLInputElement>) => {
    const str = event.target.value;
    if (!isNaN(Number(str)) && str.length >= 1) {
      if (Number(str) >= rangemax_min && Number(str) <= rangemax_max) {
      const val = Math.round(Number(str) * Math.pow(10, 5)) / Math.pow(10, 5);
        setInputRmaxErr(false);
        setFrangemax(str);
        setRangemax(val);
      } else {
        setInputRmaxErr(true);
        setFrangemax(str);
        setRangemax(undefined);
      }
    } else {
      setInputRmaxErr(true);
      setFrangemax(str);
      setRangemax(undefined);
    }
  };

  //制御レンジ下限
  const [rangemin, setRangemin] = useState<number>();
  const [frangemin, setFrangemin] = useState<number|string>();
  const [inputRminErr, setInputRminErr] = useState<boolean>(false);
  const handleChangeRangemin = (event: ChangeEvent<HTMLInputElement>) => {
    const str = event.target.value;
    if (!isNaN(Number(str)) && str.length >= 1) {
      if (Number(str) >= rangemin_min && Number(str) <= rangemin_max) {
      const val = Math.round(Number(str) * Math.pow(10, 5)) / Math.pow(10, 5);
        setInputRminErr(false);
        setFrangemin(str);
        setRangemin(val);
      } else {
        setInputRminErr(true);
        setFrangemin(str);
        setRangemin(undefined);
      }
    } else {
      setInputRminErr(true);
      setFrangemin(str);
      setRangemin(undefined);
    }
  };

  /**
   * 制御レンジ上下限値の大小関係をチェックします
   */
  function checkRange() {
    //spのみの判定
    //目標値に範囲内の数値が入っている場合、または画面表示時点の初期状態の場合
    //何か入力された後に空欄になると他の欄はエラー状態になるため、一つ前の値をprevFspに保持して判断
    if (
      (sp !== undefined && sp >= spmin && sp <= spmax) ||
      (sp === undefined && !fsp && !prevFsp)
    ) {
      setInputSpErr(false);
    } else {
      setInputSpErr(true);
    }
    //レンジ上下限値を含む判定は間接制御かつ上限・下限それぞれに入っている値が正常でないと実施されない
    if (
      //上限、下限どちらにもエラーが無い場合
      autoIndirectForm === true &&
      !inputRmaxErr &&
      !inputRminErr &&
      rangemin !== undefined &&
      rangemax !== undefined
    ) {
      if (rangemin >= rangemax) {
        setInputRmaxErr(true);
        setInputRminErr(true);
      } else {
        setInputRmaxErr(false);
        setInputRminErr(false);
        //レンジ上下限値の範囲が正常の場合目標値も確認
        if (sp !== undefined && sp <= rangemax && sp >= rangemin) {
          setInputSpErr(false);
        } else {
          setInputSpErr(true);
        }
      }
    } else if (
      //上限、下限のエラーの有無を問わない場合（値有）
      autoIndirectForm === true &&
      rangemin !== undefined &&
      rangemax !== undefined
    ) {
      if (rangemin >= rangemax) {
        setInputRmaxErr(true);
        setInputRminErr(true);
      } else {
        if (rangemax >= rangemax_min && rangemax <= rangemax_max) {
          setInputRmaxErr(false);
        }
        if (rangemin >= rangemin_min && rangemin <= rangemin_max) {
          setInputRminErr(false);
        }
        if (sp !== undefined && sp <= rangemax && sp >= rangemin) {
          setInputSpErr(false);
        } else {
          setInputSpErr(true);
        }
      }
    }
  }

  /**
   * 初期値のある値を設定します
   */
  function setInitialValue() {
    setPb(Number(fpb));
    setTi(Number(fti));
    setTd(Number(ftd));
    setPidinterval(Number(fpidinterval));
  }

  const [autoDirectCreate, setAutoDirectCreate] = useState<boolean>(false);
  const [autoIndirectCreate, setAutoIndirectCreate] = useState<boolean>(false);
  const [customCreate, setCustomCreate] = useState<boolean>(false);
  const [autoForm, setAutoForm] = useState<boolean>(true);  //初期表示
  const [autoDirectForm, setAutoDirectForm] = useState<boolean>(false);
  const [autoIndirectForm, setAutoIndirectForm]  = useState<boolean>(false);
  const [customForm, setCustomForm]  = useState<boolean>(false);
  const [autodirectInputerr, setAutodirectInputerr] = useState<boolean>(true);
  const [autoindirectInputerr, setAutoindirectInputerr] =
    useState<boolean>(true);

  useEffect(() => {
    checkInputerr();
    setButtonVisible();
  }, [
    revnum,
    learnmode,
    controlmode,
    piddatafileid,
    simulatorid,
    exp_time,
    fkdppinterval,
    sp,
    pb,
    ti,
    td,
    pidinterval,
    rangemax,
    rangemin,
    inputFkdppintervalErr,
    inputSpErr,
    inputExptimeErr,
    inputPbErr,
    inputTiErr,
    inputTdErr,
    inputRmaxErr,
    inputRminErr,
    autodirectInputerr,
    autoindirectInputerr,
    currentProjectInfo,
  ]);

  useEffect(() => {
    setForm();
  }, [learnmode, controlmode]);

  useEffect(() => {
    checkExptime();
  }, [controlmode, autoDirectForm, autoIndirectForm, fexp_time, fkdppinterval]);

  useEffect(() => {
    checkRange();
  }, [controlmode, frangemax, frangemin, fsp, autoDirectForm, autoIndirectForm]);

  /**
   * ボタン表示、非表示を切り替えるパラメータを操作します
   */
  function setButtonVisible() {
    if (
      revnum &&
      learnmode === type_auto &&
      controlmode === direct &&
      piddatafileid &&
      simulatorid &&
      fkdppinterval &&
      exp_time !== undefined &&
      sp !== undefined &&
      !autodirectInputerr
    ) {
      setAutoDirectCreate(true);
      setAutoIndirectCreate(false);
      setCustomCreate(false);
    } else if (
      revnum &&
      learnmode === type_auto &&
      controlmode === indirect &&
      piddatafileid &&
      simulatorid &&
      exp_time !== undefined &&
      sp !== undefined &&
      pb !== undefined &&
      ti !== undefined &&
      td !== undefined &&
      pidinterval &&
      rangemax !== undefined &&
      rangemin !== undefined &&
      !autoindirectInputerr
    ) {
      setAutoDirectCreate(false);
      setAutoIndirectCreate(true);
      setCustomCreate(false);
    } else if (revnum && learnmode === type_custom) {
      setAutoDirectCreate(false);
      setAutoIndirectCreate(false);
      setCustomCreate(true);
    } else {
      setAutoDirectCreate(false);
      setAutoIndirectCreate(false);
      setCustomCreate(false);
    }
  }

  /**
   * 入力フォームをモードに応じて切り替えます
   */
  function setForm() {
    if (learnmode === type_auto) {
      setAutoForm(true);
      setCustomForm(false);
      if (controlmode === direct) {
        setAutoDirectForm(true);
        setAutoIndirectForm(false);
      } else if (controlmode === indirect) {
        setAutoDirectForm(false);
        setAutoIndirectForm(true);
      }
    } else if (learnmode === type_custom) {
      setAutoForm(false);
      setAutoDirectForm(false);
      setAutoIndirectForm(false);
      setCustomForm(true);
    }
  }

  /**
   * フォームの入力エラーを確認します
   */
  function checkInputerr() {
    if (!inputExptimeErr && !inputFkdppintervalErr && !inputSpErr) {
      setAutodirectInputerr(false);
    } else {
      setAutodirectInputerr(true);
    }
    if (
      !inputExptimeErr &&
      !inputSpErr &&
      !inputPbErr &&
      !inputTiErr &&
      !inputTdErr &&
      !inputRmaxErr &&
      !inputRminErr
    ) {
      setAutoindirectInputerr(false);
    } else {
      setAutoindirectInputerr(true);
    }
  }

  //条件問わず表示する項目
  /**
   * 学習プログラムのRev選択ドロップダウンボックスを作成します
   * @returns {JSX.Element} - rev選択ドロップダウンボックス
   */
  function revSelectListCreate():JSX.Element  {
    let contents = <div />;
    contents =(
      <div className="contents">
        <div className="contents">{t("fkdpp_settings_revnum")}</div>
        <FormControl required sx={{ mr: 1, width: 1 / 2 }}>
          <InputLabel id="rev-label">{t("fkdpp_settings_revnum")}</InputLabel>
            <Select
              labelId="rev-label"
              id="rev"
              value={revnum}
              label={t("fkdpp_settings_revnum")}
              MenuProps={MenuProps}
              onChange={handleChangeRevnum}
            >
              {revList}
            </Select>
        </FormControl>
      </div>
    );
    return contents;
  }
  const revSelectList = revSelectListCreate();

  
  /**
   * 学習モード選択ラジオボタンを作成します
   * @returns {JSX.Element} - 学習モード選択ラジオボタン
   */
  function createLearnmodeButton(): JSX.Element {
    let contents = <div />;
    contents = (
      <div className="contents">
        <div className="contents">{t("fkdpp_settings_mode")}</div>
        <FormControl sx={{ mr: 1 }}>
          <RadioGroup
            row
            aria-label="learnmode"
            name="mode"
            value={learnmode}
            onChange={handleChangeLearnmode}
          >
            <Box sx={{ width: 200, p: 1 }}>
              <FormControlLabel
                value={type_auto}
                control={<Radio />}
                label={label_auto}
              />
            </Box>
            <Box sx={{ width: 200, p: 1 }}>
              <FormControlLabel
                value={type_custom}
                control={<Radio />}
                label={label_custom}
              />
            </Box>
          </RadioGroup>
        </FormControl>
      </div>
    );
    return contents;
  }
  const learnmodeButton = createLearnmodeButton();

  //学習条件自動モード…controlmode, piddatafileid, simulatorid, sp, exp_time
  /**
   * 制御モード選択ドロップダウンボックスを作成します
   * @returns {JSX.Element} - 制御モード選択ドロップダウンボックス
   */
  function createControlModeList():JSX.Element {
    let contents = <div />
    contents = (
      <div className="contents">
        <div className="contents">{t("fkdpp_settings_controlmode")}</div>
        <FormControl required sx={{ mr: 1, width: 1 / 2 }}>
          <InputLabel id="controlmode-label">{t("fkdpp_settings_controlmode")}</InputLabel>
          <Select
            labelId="controlmode-label"
            id="controlmode"
            value={controlmode}
            label={t("fkdpp_settings_controlmode")}
            MenuProps={MenuProps}
            onChange={handleChangeControlmode}
          >
            <MenuItem value={direct}>{t("fkdpp_settings_direct")}</MenuItem>
            <MenuItem value={indirect}>{t("fkdpp_settings_indirect")}</MenuItem>
          </Select>
        </FormControl>
      </div>
    );
    return contents;
  }
  const controlModeList = autoForm === true ? createControlModeList() : "";

  /**
   * PIDデータファイル選択ドロップダウンボックスを作成します
   * @returns {JSX.Element} - PIDデータファイル選択ドロップダウンボックス
   */
  function createPiddataFileIdList():JSX.Element {
    let contents = <div />
    contents = (
      <div className="contents">
        <div className="contents">{t("fkdpp_settings_pidconfile")}</div>
        <FormControl required sx={{ mr: 1, width: 1 / 2 }}>
        <InputLabel id="pidconfile-label">{t("fkdpp_settings_pidconfile")}</InputLabel>
          <Select
            labelId="pidconfile-label"
            id="pidconfile"
            value={piddatafileid}
            label={t("fkdpp_settings_pidconfile")}
            MenuProps={MenuProps}
            onChange={handleChangePiddataFileId}
          >
          {fileList}
        </Select>
      </FormControl>
      </div>
    );
    return contents;
  }
  const piddataFileIdList = autoForm === true ? createPiddataFileIdList() : "";

  /**
   * シミュレータ選択ドロップダウンボックスを作成します
   * @returns {JSX.Element} - シミュレータ選択ドロップダウンボックス
   */
  function createSimulatorIdList():JSX.Element {
    let contents = <div />
    contents = (
      <div className="contents">
        <div className="contents">{t("fkdpp_settings_simulator")}</div>
        <FormControl required sx={{ mr: 1, width: 1 / 2 }}>
          <InputLabel id="simulator-label">{t("fkdpp_settings_simulator")}</InputLabel>
          <Select
            labelId="simulator-label"
            id="simulator"
            value={simulatorid}
            label={t("fkdpp_settings_simulator")}
            MenuProps={MenuProps}
            onChange={handleChangeSimulatorId}
          >
            {simulatorList}
          </Select>
        </FormControl>
      </div>
    );
    return contents;
  }
  const simulatorIdList = autoForm === true ? createSimulatorIdList() : "";
 
  /**
   * 目標値設定フォームを作成します
   * @returns {JSX.Element} - 目標値設定フォーム
   */
  function createSpForm():JSX.Element {
    let contents = <div />
    contents = (autoForm === true || autoDirectForm === true) && autoIndirectForm === false ? (
      <div className="contents">
        <div className="contents">{t("fkdpp_settings_sp")}</div>
        <FormControl sx={{ mr: 1, width: 1 / 2 }}>
          <TextField
            required
            id="sp"
            label={t("fkdpp_settings_sp")}
            variant="outlined"
            inputProps={{ maxLength: 14 }}
            value={fsp}
            error={inputSpErr}
            helperText={t("fkdpp_settings_sp_error")}
            onChange={handleChangeSp}
            onBlur={() => setFsp(truncation(fsp, 5, inputSpErr))}
          />
        </FormControl>
      </div>
    ) : (
      <div className="contents">
        <div className="contents">{t("fkdpp_settings_sp")}</div>
        <FormControl sx={{ mr: 1, width: 1 / 2 }}>
          <TextField
            required
            id="sp"
            label={t("fkdpp_settings_sp")}
            variant="outlined"
            inputProps={{ maxLength: 14 }}
            value={fsp}
            error={inputSpErr}
            helperText={t("fkdpp_settings_sp_error_indirect")}
            onChange={handleChangeSp}
            onBlur={() => setFsp(truncation(fsp, 5, inputSpErr))}
          />
        </FormControl>
      </div>
    );
    return contents;
  }
  const spForm = autoForm === true ? createSpForm() : "";
  
  /**
   * 実験時間設定フォームを作成します
   * @returns {JSX.Element} - 実験時間設定フォーム
   */
  function createExptimeForm():JSX.Element {
    let contents = <div />
    let max = 0;
    let min = 0;
    if (fkdppinterval) {
      min = Math.round(fkdppinterval / 1000 * 1 * Math.pow(10, 2)) / Math.pow(10, 2);
      max = Math.round(fkdppinterval / 1000 * 10000 * Math.pow(10, 2)) / Math.pow(10, 2);
    }
    contents = (autoForm === false || autoDirectForm === false) &&
    autoIndirectForm === true ? (
      <div className="contents">
        <div className="contents">{t("fkdpp_settings_exptime")}</div>
        <FormControl sx={{ mr: 1, width: 1 / 2 }}>
          <TextField
            required
            id="exp_time"
            label={t("fkdpp_settings_exptime")}
            variant="outlined"
            inputProps={{ maxLength: 10 }}
            value={fexp_time}
            error={inputExptimeErr}
            helperText={t("fkdpp_settings_exptime_indirect_error")}
            onChange={handleChangeExptime}
            onBlur={() => setFexp_time(truncation(fexp_time, 2, inputExptimeErr))}
          />
        </FormControl>
      </div>
    ) : (
      <div className="contents">
        <div className="contents">{t("fkdpp_settings_exptime")}</div>
        <FormControl sx={{ mr: 1, width: 1 / 2 }}>
          <TextField
            required
            id="exp_time"
            label={t("fkdpp_settings_exptime")}
            variant="outlined"
            inputProps={{ maxLength: 10 }}
            value={fexp_time}
            error={inputExptimeErr}
            helperText={t("fkdpp_settings_exptime_direct_error") + "(" + min + " - " + max + ")"}
            onChange={handleChangeExptime}
            onBlur={() => setFexp_time(truncation(fexp_time, 2, inputExptimeErr))}
          />
        </FormControl>
      </div>
    );
    return contents;
  }
  const exptimeForm = autoForm === true ? createExptimeForm() : "";

  //学習条件自動モード+制御モード直接制御...fkdppinterval 初期状態の時も表示しておく
  /**
   * FKDPP制御周期設定フォームを作成します
   * @returns {JSX.Element} - FKDPP制御周期設定フォーム
   */
  function createFkdppIntervalForm():JSX.Element {
    let contents = <div />;
    contents = (
      <div className="contents">
        <div className="contents">{t("fkdpp_settings_fkdppinterval")}</div>
        <FormControl sx={{ mr: 1, width: 1 / 2 }}>
          <TextField
            required
            id="fkdppinterval"
            label={t("fkdpp_settings_fkdppinterval")}
            variant="outlined"
            inputProps={{ maxLength: 5 }}
            value={ffkdppinterval}
            error={inputFkdppintervalErr}
            helperText={t("fkdpp_settings_fkdppinterval_error")}
            onChange={handleChangeFkdppinterval}
            onBlur={() => setFfkdppinterval(truncation(ffkdppinterval, 0, inputFkdppintervalErr))}
            />
        </FormControl>
      </div>
    );
    return contents;
  }
  const fkdppIntervalForm =
    (autoForm === true || autoDirectForm === true) && autoIndirectForm === false
      ? createFkdppIntervalForm()
      : "";

  //学習条件自動モード+制御モード間接制御...pidtitle, pb, ti, td, pidinterval, rangemax, rangemin
  /**
   * PID設定のタイトルを作成します
   * @returns {JSX.Element} - タイトル
   */
  function createPidTitle(): JSX.Element {
    let contents = <div />
    contents = (
      <div className="pid-title">
        <h4>{t("fkdpp_settings_pidtitle")}</h4>
      </div>
    );
    return contents;
  }
  const pidTitle = autoIndirectForm === true ? createPidTitle() : "";

  /**
   * 比例帯入力フォームを作成します
   * @returns {JSX.Element} - 比例帯入力フォーム
   */
  function createPbForm():JSX.Element {
    let contents = <div />;
    contents = (
      <div className="contents">
        <div className="contents">{t("fkdpp_settings_pb")}</div>
        <FormControl sx={{ mr: 1, width: 1 / 2 }}>
          <TextField
            required
            id="pb"
            label={t("fkdpp_settings_pb")}
            variant="outlined"
            inputProps={{ maxLength: 5 }}
            value={fpb}
            error={inputPbErr}
            helperText={t("fkdpp_settings_pb_error")}
            onChange={handleChangePb}
            onBlur={() => setFpb(truncation(fpb, 1, inputPbErr))}
          />
        </FormControl>
      </div>
    );
    return contents;
  }
  const pbForm = autoIndirectForm === true ? createPbForm() : "";

  /**
   * 積分時間入力フォームを作成します
   * @returns {JSX.Element} - 積分時間入力フォーム
   */
  function createTiForm():JSX.Element {
    let contents = <div />;
    contents = (
      <div className="contents">
        <div className="contents">{t("fkdpp_settings_ti")}</div>
        <FormControl sx={{ mr: 1, width: 1 / 2 }}>
          <TextField
            required
            id="ti"
            label={t("fkdpp_settings_ti")}
            variant="outlined"
            inputProps={{ maxLength: 4}}
            value={fti}
            error={inputTiErr}
            helperText={t("fkdpp_settings_ti_error")}
            onChange={handleChangeTi}
            onBlur={() => setFti(truncation(fti, 0, inputTiErr))}
          />
        </FormControl>
      </div>
    );
    return contents;
  }
  const tiForm = autoIndirectForm === true ? createTiForm() : "";

  /**
   * 微分時間入力フォームを作成します
   * @returns {JSX.Element} - 微分時間入力フォーム
   */
  function createTdForm():JSX.Element {
    let contents = <div />;
    contents = (
      <div className="contents">
        <div className="contents">{t("fkdpp_settings_td")}</div>
        <FormControl sx={{ mr: 1, width: 1 / 2 }}>
          <TextField
            required
            id="td"
            variant="outlined"
            label={t("fkdpp_settings_td")}
            inputProps={{ maxLength: 4 }}
            value={ftd}
            error={inputTdErr}
            helperText={t("fkdpp_settings_td_error")}
            onChange={handleChangeTd}
            onBlur={() => setFtd(truncation(ftd, 0, inputTdErr))}
          />
        </FormControl>
      </div>
    );
    return contents;
  }
  const tdForm = autoIndirectForm === true ? createTdForm() : "";

  /**
   * PID制御時間選択ドロップダウンボックスを作成します
   * @returns {JSX.Element} - PID制御時間選択ドロップダウンボックス
   */
  function createPidintervalForm():JSX.Element {
    let contents = <div />;
    contents = (
      <div className="contents">
        <div className="contents">{t("fkdpp_settings_pidinterval")}</div>
        <FormControl required sx={{ mr: 1, width: 1 / 2 }}>
          <InputLabel id="pidinterval">{t("fkdpp_settings_pidinterval")}</InputLabel>
          <Select
            labelId="pidinterval"
            id="pidinterval"
            value={fpidinterval}
            label={t("fkdpp_settings_pidinterval")}
            MenuProps={MenuProps}
            onChange={handleChangePidinterval}
          >
            {intervalOptionsList}
          </Select>
        </FormControl>
      </div>
    );
    return contents;
  }
  const pidintervalForm =
    autoIndirectForm === true ? createPidintervalForm() : "";

  /**
   * 制御レンジ上限入力フォームを作成します
   * @returns {JSX.Element} - 制御レンジ上限入力フォーム
   */
  function createRangemaxForm():JSX.Element {
    let contents = <div />;
    contents = (
      <div className="contents">
        <div className="contents">{t("fkdpp_settings_rangemax")}</div>
        <FormControl sx={{ mr: 1, width: 1 / 2 }}>
          <TextField
            required
            id="rangemax"
            label={t("fkdpp_settings_rangemax")}
            variant="outlined"
            inputProps={{ maxLength: 14 }}
            value={frangemax}
            error={inputRmaxErr}
            helperText={t("fkdpp_settings_rangemax_error")}
            onChange={handleChangeRangemax}
            onBlur={() => setFrangemax(truncation(frangemax, 5, inputRmaxErr))}
          />
        </FormControl>
      </div>
    );
    return contents;
  }
  const rangemaxForm = autoIndirectForm === true ? createRangemaxForm() : "";

  /**
   * 制御レンジ下限入力フォームを作成します
   * @returns {JSX.Element} - 制御レンジ下限入力フォーム
   */
  function createRangeminForm():JSX.Element {
    let contents = <div />;
    contents = (
      <div className="contents">
        <div className="contents">{t("fkdpp_settings_rangemin")}</div>
        <FormControl sx={{ mr: 1, width: 1 / 2 }}>
          <TextField
            required
            id="rangemin"
            label={t("fkdpp_settings_rangemin")}
            variant="outlined"
            inputProps={{ maxLength: 14 }}
            value={frangemin}
            error={inputRminErr}
            helperText={t("fkdpp_settings_rangemin_error")}
            onChange={handleChangeRangemin}
            onBlur={() => setFrangemin(truncation(frangemin, 5, inputRminErr))}
          />
        </FormControl>
      </div>
    );
    return contents;
  }
  const rangeminForm = autoIndirectForm === true ? createRangeminForm() : "";

  //詳細設定モード
  /**
   * fkdppconfファイル設定ボタン群を作成します
   * アップロード、ダウンロード、リセットの3種のボタンから構成されます
   * @returns {JSX.Element} - fkdppconfファイル設定ボタン群
   */
  function createFkdppconfButton():JSX.Element {
    let contents = <div />;
    let filename = <div />;
    filename = <Box sx ={{width: 1 / 2, p:1}}>{t("name_configfile")}{fkdppconffilename}</Box>;
    contents = (
      <div className="contents">
        <Box sx ={{width: 250, p:1}}>{t("fkdpp_settings_fkdppconf")}</Box>       
        <UploadSettingFileButton onChange={(e) => {handleSelectConfFile(e, fkdppconf);}}/>               
        <DownloadSFileButton onClick={() => handleDownloadConfFile(fkdppconf)}/>
        <ResetFileButton onClick={() => handleResetConfFile(fkdppconf)}/> 
        {filename}
      </div>
    );
    return contents;
  }
  const fkdppconfButton =
    customForm === true && uploading === false ? createFkdppconfButton() : "";

  /**
   * simconfファイル設定ボタン群を作成します
   * アップロード、ダウンロード、リセットの3種のボタンから構成されます
   * @returns {JSX.Element} - simconf設定ボタン群
   */
  function createSimconfButton():JSX.Element {
    let contents = <div />;
    let filename = <div />;
    filename = <Box sx ={{width: 1 / 2, p:1}}>{t("name_configfile")}{simconffilename}</Box>;
    contents = (
      <div className="contents">
        <Box sx ={{width: 250, p:1}}>{t("fkdpp_settings_simconf")}</Box>
        <UploadSettingFileButton onChange={(e) => {handleSelectConfFile(e, simconf);}}/>               
        <DownloadSFileButton onClick={() => handleDownloadConfFile(simconf)}/>
        <ResetFileButton onClick={() => handleResetConfFile(simconf)}/> 
        {filename}
      </div>
    );
    return contents;
  }
  const simconfButton =
    customForm === true && uploading === false ? createSimconfButton() : "";

  /**
   * rewardconfファイル設定ボタン群を作成します
   * アップロード、ダウンロード、リセットの3種のボタンから構成されます
   * @returns {JSX.Element} - rewardconf設定ボタン群
   */
  function createRewardconfButton():JSX.Element {
    let contents = <div />;
    let filename = <div />;
    filename = <Box sx ={{width: 1 / 2, p:1}}>{t("name_configfile")}{rewardconffilename}</Box>;
    contents = (
      <div className="contents">
        <Box sx ={{width: 250, p:1}}>{t("fkdpp_settings_rewardconf")}</Box>
        <UploadSettingFileButton onChange={(e) => {handleSelectConfFile(e, rewardconf);}}/>               
        <DownloadSFileButton onClick={() => handleDownloadConfFile(rewardconf)}/>
        <ResetFileButton onClick={() => handleResetConfFile(rewardconf)}/> 
        {filename}
      </div>
    );
    return contents;
  }
  const rewardconfButton =
    customForm === true && uploading === false ? createRewardconfButton() : "";

  //各条件時の実行ボタン
  /**
   * 自動モード+直接制御時の実行ボタンを作成します
   * @returns {JSX.Element} - 自動モード+直接制御時の実行ボタン
   */
  function createAutoDirectModeButton():JSX.Element {
    let contents = <div />;
    if (autoDirectCreate === true) {
      contents = (
        <Button
          id="auto-direct-create"
          className="btn-mainspace"
          onClick={() => handleTrainModel(type_auto + direct)}
        >
          {t("fkdpp_settings_create_directmode")}
        </Button>
      );
    } else {
      contents = (
        <Button id="auto-direct-create" className="btn-mainspace" disabled>
          {t("fkdpp_settings_create_directmode")}
        </Button>
      );
    }
    return contents;
  }
  const autoDirectModeButton =
    (learnmode === type_auto && controlmode === direct) ||
    (learnmode === type_auto && controlmode === "")
      ? createAutoDirectModeButton()
      : "";

  /**
   * 自動モード+間接制御時の実行ボタンを作成します
   * @returns {JSX.Element} - 自動モード+間接制御時の実行ボタン
   */
  function createAutoIndirectModeButton():JSX.Element {
    let contents = <div />;
    if (autoIndirectCreate === true) {
      contents = (
        <Button
          id="auto-indirect-create"
          className="btn-mainspace"
          onClick={() => handleTrainModel(type_auto + indirect)}
        >
          {t("fkdpp_settings_create_indirectmode")}
        </Button>
      );
    } else {
      contents = (
        <Button id="auto-indirect-create" className="btn-mainspace" disabled>
          {t("fkdpp_settings_create_indirectmode")}
        </Button>
      );
    }
    return contents;
  }
  const autoIndirectModeButton =
    learnmode === type_auto && controlmode === indirect
      ? createAutoIndirectModeButton()
      : "";

  /**
   * 詳細設定モード時の実行ボタンを作成します
   * @returns {JSX.Element} - 詳細設定モード時の実行ボタン
   */
  function createCustomModeButton():JSX.Element {
    let contents = <div />;
    if (customCreate === true) {
      contents = (
        <Button
          id="custom-create"
          className="btn-mainspace"
          onClick={() => {
            handleTrainModel(type_custom);
          }}
        >
          {t("fkdpp_settings_create_custommode")}
        </Button>
      );
    } else {
      contents = (
        <Button id="custom-create" className="btn-mainspace" disabled>
          {t("fkdpp_settings_create_custommode")}
        </Button>
      );
    }
    return contents;
  }
  const customModeButton =
    learnmode === type_custom ? createCustomModeButton() : "";

  /**
   * 学習中止ボタンを作成します
   * @returns {JSX.Element} - 学習中止ボタン
   */
  function createStopTrainButton(): JSX.Element {
    let contents = <div />
    contents = (
      <Button 
        id="study-stop"
        className="btn-mainspace" 
        onClick={handleStopTrainModel}>
        {t("fkdpp_settings_stop")}
      </Button>
    );
    return contents;
  }
  const stopTrainButton =
    currentProjectInfo?.fkdpplearnsts === StatusDoing
      ? createStopTrainButton()
      : "";

  /**
   * 実行ボタン押下時に最新のモデル学習状況を取得します。
   * 学習中の場合、学習モデルが存在する場合にはダイアログを表示します
   * @function handleTrainModel
   * @param {string} type - 学習条件と制御モード（AutoDirect/AutoIndirect/Custom）
   */
  function handleTrainModel(type: string) {
    updateProjectInfo();
    resetUnoperatedTimeout();
    let count = 0;
    onLoading(true);
    getProjects({
      authService: authService,
      success: (rows) => {
        const newInfo = rows.find((row) => {
          if (row.projectid === projectid) {
            return row;
          }
        });
        rows.forEach((row) => {
          if (row.fkdpplearnsts === StatusDoing) {
            count = count + 1;
          }
        });
        if (newInfo) {
          setCurrentProjectInfo(newInfo);
          setTimeout(() => {
            if (newInfo.fkdpplearnsts === StatusDoing) {
              showMessage({
                title: t("fkdpp_train_doing"),
                message: t("fkdpp_train_doing_caption"),
              });
            } else if (newInfo.simcreatests === StatusDoing) {
              showMessage({
                title: t("simcreatests_doing"),
                message: t("simcreatests_doing_message"),
              });
            } else if (newInfo.simevalsts === StatusDoing) {
              showMessage({
                title: t("simevalsts_doing"),
                message: t("simevalsts_doing_message"),
              });
            } else if (newInfo.fkdppevalsts === StatusDoing) {
              showMessage({
                title: t("fkdppevalsts_doing"),
                message: t("fkdppevalsts_doing_message"),
              });
            } else if (newInfo.fkdppsimsts === StatusDoing) {
              showMessage({
                title: t("fkdppsimsts_doing"),
                message: t("fkdppsimsts_doing_message"),
              });
            } else if (count > 0) {
              showMessage({
                title: t("fkdppsts_all_doing"),
                message: t("fkdppsts_all_doing_message"),
              });
            } else if (newInfo.fkdpplearnsts === StatusDone) {
              showConfirm({
                title: t("fkdpp_train_done"),
                message: t("fkdpp_train_done_caption"),
                onOk: () => {
                  showTrainConfirmDialog(type);
                },
              });
            } else {
              showTrainConfirmDialog(type);
            }
          });
        }
      },
      fail: (id, status) => {
        onCommunicationError(id, status), onLoading(false);
      },
    });
  }

  /**
   * モデル学習の設定確認ダイアログを表示します
   * @function showTrainConfirmDialog
   * @param {string} type - 学習条件と制御モード（AutoDirect/AutoIndirect/Custom）
   */
  function showTrainConfirmDialog(type: string) {
    if (type === type_auto + direct) {
      const selectedsimulator = simulatorsInfo.find((sim) => {
          return sim.simulatorid === simulatorid;
      });
      const selectedpiddatafile = rows.find((row) => {
          return row.fileid === piddatafileid;
      });
      showConfirm({
        title: t("fkdpp_train_confirm"),
        message: t("fkdpp_train_confirm_message") + "\n"
          + t("fkdpp_settings_revnum") + ": " + revnum + "\n"
          + t("fkdpp_settings_mode") + ": " + label_auto + "\n"
          + t("fkdpp_settings_controlmode") + ": " + t("fkdpp_settings_direct") + "\n"
          + t("fkdpp_settings_pidconfile") + ": " + selectedpiddatafile?.name + "\n"
          + t("fkdpp_settings_simulator") + ": " + selectedsimulator?.name + "\n"
          + t("fkdpp_settings_fkdppinterval") + ": " + fkdppinterval + " [msec]\n"
          + t("fkdpp_settings_exptime") + ": " + exp_time + "\n"
          + t("fkdpp_settings_sp") + ": " + sp + "\n",
        onOk: () => {
          startTrainModel(type);
        }
      });
    } else if (type === type_auto + indirect) {
      const selectedsimulator = simulatorsInfo.find((sim) => {
        return sim.simulatorid === simulatorid;
      });
      const selectedpiddatafile = rows.find((row) => {
        return row.fileid === piddatafileid;
      });
      showConfirm({
        title: t("fkdpp_train_confirm"),
        message: t("fkdpp_train_confirm_message") + "\n"
          + t("fkdpp_settings_revnum") + ": " + revnum + "\n"
          + t("fkdpp_settings_mode") + ": " + label_auto + "\n"
          + t("fkdpp_settings_controlmode") + ": " + t("fkdpp_settings_indirect") + "\n"
          + t("fkdpp_settings_pidconfile") + ": " + selectedpiddatafile?.name + "\n"
          + t("fkdpp_settings_simulator") + ": " + selectedsimulator?.name + "\n"
          + t("fkdpp_settings_exptime") + ": " + exp_time + "\n"
          + "<" + t("fkdpp_settings_pidtitle") + ">\n"
          + t("fkdpp_settings_sp") + ": " + sp + "\n"
          + t("fkdpp_settings_pb") + ": " + pb + " [%]\n"
          + t("fkdpp_settings_ti") + ": " + ti + " [sec]\n"
          + t("fkdpp_settings_td") + ": " + td + " [sec]\n"
          + t("fkdpp_settings_pidinterval") + ": " + pidinterval + " [msec]\n"
          + t("fkdpp_settings_rangemax") + ": " + rangemax + "\n"
          + t("fkdpp_settings_rangemin") + ": " + rangemin + "\n",
        onOk: () => {
          startTrainModel(type);
        }
      });
    } else {
      showConfirm({
        title: t("fkdpp_train_confirm"),
        message: t("fkdpp_train_confirm_message") + "\n"
          + t("fkdpp_settings_revnum") + ": " + revnum + "\n"
          + t("fkdpp_settings_mode") + ": " + label_custom + "\n",
        onOk: () => {
          startTrainModel(type);
        }
      });
    }
  }

  /**
   * モデル学習を開始します
   * @function startTrainModel
   * @param {string} type - 学習条件と制御モード（AutoDirect/AutoIndirect/Custom）
   */
  function startTrainModel(type: string) {
    resetUnoperatedTimeout();
    trainModel({
      authService: authService,
      projectid: projectid,
      type: type,
      version: revnum,
      learnmode: learnmode,
      controlmode: controlmode,
      piddatafileid: piddatafileid,
      simulatorid: simulatorid,
      fkdppinterval: fkdppinterval,
      sp: sp,
      exp_time: exp_time,
      pb: pb,
      ti: ti,
      td: td,
      pidinterval: pidinterval,
      rangemax: rangemax,
      rangemin: rangemin,
      success: () => {
        onLoading(false);
        onTop();
      },
      fail: (id, status) => {
        onLoading(false);
        onCommunicationError(id, status);
      },
    });
  }

  /**
   * 実行中のモデル学習を中止します。
   * 中止ボタン押下時に最新のモデル学習状況を取得するため、ページ表示時とステータスが変わった場合は
   * 画面を更新したことを知らせるダイアログが表示されます（doing -> done時）。
   * よって、モデル学習中でない場合は中止リクエストを送信しません。
   */
  function handleStopTrainModel() {
    updateProjectInfo();
    resetUnoperatedTimeout();
    getProjects({
      authService: authService,
      projectid: projectid,
      success: (rows) => {
        const row = rows[0];
        setCurrentProjectInfo(row);
        onLoading(false);
        if (row.fkdpplearnsts !== StatusDoing) {
          showMessage({
            title: t("fkdpp_train_update"),
            message: t("fkdpp_train_update_caption"),
          });
        } else {
          showConfirm({
            title: t("fkdpp_train_stop"),
            message: t("fkdpp_train_stop_caption"),
            onOk: () => { 
              onLoading(true);
              stopTrainModel({
                authService: authService,
                projectid: projectid,
                success: () => {
                  updateProjectInfo();
                  setTimeout(() => {
                    onLoading(false);
                  });
                },
                fail: (id, status) => {
                  onLoading(false);
                  onCommunicationError(id, status);
                },
              })},
          });
        }
      },
      fail: (id, status) => {
        onCommunicationError(id, status),
        onLoading(false);
      },
    });
  }
  
  return (
    <div className="mainspace">
      <div className="mainspace-title">
        <h3>{t("project_detail_project_name")}</h3>
        <h3 style={{ marginLeft: "10px" }}>{projectInfo.name}</h3>
      </div>
      <div className="mainspace-toolbar">
        <BackButton onClick={onBack} />
        {stopTrainButton}
      </div>
      <div className="mainspace-toolbar">
        <h3>{t("fkdpp_train_settings")}</h3>
      </div>
      <div className="test-form">
        {revSelectList}
        {learnmodeButton}

        {fkdppconfButton}
        {simconfButton}
        {rewardconfButton}
        {customModeButton}

        {controlModeList}
        {piddataFileIdList}
        {simulatorIdList}        
        {fkdppIntervalForm}
        {exptimeForm}

        {pidTitle}
        {spForm}

        {autoDirectModeButton}

        {pbForm}
        {tiForm}
        {tdForm}
        {pidintervalForm}
        {rangemaxForm}
        {rangeminForm}
        {autoIndirectModeButton}
      </div>
    </div>
  );
}

